import { useEffect, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import MovieCard from "@/components/feature/MovieCard";
import { searchMovies, sortByUpdated, typeLabel } from "@/lib/movieData";

const typeFilters = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const type = searchParams.get("type") || "all";
  const [keyword, setKeyword] = useState(query);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  usePageMeta({
    title: query ? `搜索「${query}」- 最近中文字幕` : "搜索影视资源 - 最近中文字幕",
    description: query
      ? `在最近中文字幕搜索「${query}」相关的中文字幕影视资源，涵盖电影与电视剧，快速查看作品简介与字幕更新情况。`
      : "搜索最近中文字幕站内的影视资源，支持按关键词检索电影与电视剧的中文字幕信息。",
    keywords: "最近中文字幕,搜索影视,中文字幕电影,中文字幕电视剧,影视资源检索",
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    const next = new URLSearchParams();
    if (value) next.set("q", value);
    if (type !== "all") next.set("type", type);
    setSearchParams(next);
  };

  const changeType = (key: string) => {
    const next = new URLSearchParams(searchParams);
    if (key === "all") next.delete("type");
    else next.set("type", key);
    setSearchParams(next);
  };

  const hasQuery = query.trim().length > 0;
  let results = hasQuery ? searchMovies(query) : [];
  if (type !== "all") results = results.filter((item) => item.type === type);
  results = sortByUpdated(results);

  return (
    <div className="w-full px-4 md:px-6 py-8 md:py-10">
      <header className="mb-6 md:mb-8">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {hasQuery ? `搜索「${query}」` : "搜索影视资源"}
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          {hasQuery
            ? `共找到 ${results.length} 条与中文字幕相关的影视作品`
            : "输入片名、导演、主演或题材关键词，快速检索带中文字幕的影视作品。"}
        </p>

        <form onSubmit={submit} className="mt-5 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
            <input
              type="text"
              name="q"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索片名 / 导演 / 主演 / 题材"
              className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-9 pr-4 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>
          <button
            type="submit"
            className="rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950 whitespace-nowrap"
          >
            搜索
          </button>
        </form>

        {hasQuery && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-sm text-foreground-500">类别：</span>
            <div className="flex rounded-full bg-background-100 p-1">
              {typeFilters.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => changeType(item.key)}
                  className={`rounded-full px-4 py-1.5 text-sm transition-colors whitespace-nowrap ${
                    type === item.key
                      ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                      : "text-foreground-700 hover:text-primary-700"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </header>

      {!hasQuery ? (
        <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
          <i className="ri-search-eye-line text-5xl text-foreground-300"></i>
          <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
            开始你的搜索
          </h2>
          <p className="mt-2 text-sm text-foreground-500">
            例如搜索「科幻」「悬疑」或某位导演的名字。
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            {["科幻", "悬疑", "爱情", "古装"].map((tag) => (
              <Link
                key={tag}
                to={`/search?q=${encodeURIComponent(tag)}`}
                className="rounded-full border border-secondary-200 bg-secondary-50 px-3 py-1.5 text-sm text-secondary-900 transition-colors hover:border-accent-300 hover:bg-accent-100"
              >
                {tag}
              </Link>
            ))}
          </div>
        </div>
      ) : results.length === 0 ? (
        <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
          <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
            没有找到相关作品
          </h2>
          <p className="mt-2 text-sm text-foreground-500">
            换个关键词试试，或回到首页浏览最新更新。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 dark:text-foreground-950 whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      ) : (
        <>
          <div className="mb-4 text-sm text-foreground-500">
            共 <strong className="text-foreground-900">{results.length}</strong> 条结果
            {type !== "all" && ` · ${typeLabel(type === "tv" ? "tv" : "movie")}`}
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
            {results.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}